import React, { useEffect, useState } from 'react';
import { Check, Loader2, Lock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/translations';
import { useEventDetails } from '@/panel/pages/event-details/EventDetailsContext';
import { ComponentType, EVENT_SETTINGS, EventSettingsKey, isSupportedLanguageValue } from '@/panel/pages/event-details/settingsSchema';

const EventSettings: React.FC = () => {
  const { panelRole, getSettingValue, updateSetting, toggleSetting, saveLanguageSetting, savingLanguage } = useEventDetails();
  const [editDraft, setEditDraft] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const savedLanguageValue = getSettingValue('lang');
  const currentLanguage = isSupportedLanguageValue(savedLanguageValue) ? savedLanguageValue : DEFAULT_LANGUAGE;
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(currentLanguage);

  const normalizeInputValue = (value: string) => value.trim();

  const isValidHttpUrl = (value: string) => {
    const normalized = normalizeInputValue(value);
    if (!normalized) return true;

    try {
      const parsed = new URL(normalized);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isInputValueValid = (key: EventSettingsKey, value: string) => {
    if (key === 'bloomdate_url') {
      return isValidHttpUrl(value);
    }

    return true;
  };

  const saveInputSetting = async (key: EventSettingsKey, options?: { closeEditor?: boolean }) => {
    const normalizedValue = key === 'bloomdate_url' ? normalizeInputValue(editDraft) : editDraft;
    const isValid = isInputValueValid(key, normalizedValue);

    if (!isValid) {
      return false;
    }

    const saved = await updateSetting(key, normalizedValue);
    if (saved) {
      setEditDraft(normalizedValue);
      if (options?.closeEditor) {
        setEditingKey(null);
      }
    }

    return saved;
  };

  useEffect(() => {
    setSelectedLanguage(currentLanguage);
  }, [currentLanguage]);

  const languageDirty = selectedLanguage !== currentLanguage;

  const renderAdminRow = (key: EventSettingsKey, title: string, description: string, componentType: ComponentType) => {
    const value = getSettingValue(key);
    const isOn = value === 'true';

    return (
      <div key={key} className="rounded-2xl border border-indigo-100 bg-white/90 px-5 py-4 shadow-sm flex items-center justify-between">
        <div className="pr-4">
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-sm text-slate-500">{description}</div>
        </div>
        <div className="shrink-0">
          {componentType === 'input' ? (
            <span className="text-sm text-slate-600">{value && value !== 'false' ? value : '—'}</span>
          ) : (
            isOn ? <Check className="h-7 w-7 text-green-600" /> : <Lock className="h-7 w-7 text-amber-500" />
          )}
        </div>
      </div>
    );
  };

  const renderLanguageRow = () => {
    if (panelRole !== 'superadmin') return null;

    return (
      <div key="lang" className="rounded-2xl border border-indigo-100 bg-white/90 px-5 py-4 shadow-sm">
        <div className="space-y-4">
          <div>
            <div className="font-semibold text-slate-900">Idioma</div>
            <div className="text-sm text-slate-500">Idioma principal del album.</div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Select
              value={selectedLanguage}
              onValueChange={(value) => {
                if (isSupportedLanguageValue(value)) {
                  setSelectedLanguage(value);
                }
              }}
            >
              <SelectTrigger
                className="w-full sm:w-[180px]"
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  const expanded = (event.currentTarget as HTMLElement).getAttribute('aria-expanded');
                  if (expanded !== 'true' && languageDirty && !savingLanguage) {
                    event.preventDefault();
                    void saveLanguageSetting(selectedLanguage);
                  }
                }}
              >
                <SelectValue placeholder={currentLanguage} />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((language) => (
                  <SelectItem key={language} value={language}>
                    {language}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {languageDirty && (
              <Button
                size="sm"
                onClick={() => { void saveLanguageSetting(selectedLanguage); }}
                disabled={savingLanguage}
                className="sm:shrink-0"
              >
                {savingLanguage ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Guardando…
                  </span>
                ) : (
                  'Guardar'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSuperadminRow = (
    key: EventSettingsKey,
    title: string,
    description: string,
    componentType: ComponentType,
    readOnly?: boolean,
  ) => {
    const value = getSettingValue(key);

    if (componentType === 'input') {
      const isEditing = editingKey === key;
      const isReadOnly = !!readOnly || description.includes('[PRÓXIMAMENTE]');
      const isInvalid = isEditing ? !isInputValueValid(key, editDraft) : false;

      return (
        <div key={key} className="rounded-2xl border border-indigo-100 bg-white/90 px-5 py-4 shadow-sm">
          <div className="space-y-4">
            <div>
              <div className="font-semibold text-slate-900">{title}</div>
              <div className="text-sm text-slate-500">{description}</div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {isEditing ? (
                <>
                  <div className="relative flex-1">
                    <Input
                      value={editDraft}
                      onChange={(event) => setEditDraft(event.target.value)}
                      onBlur={(event) => {
                        const nextTarget = event.relatedTarget as HTMLElement | null;
                        if (nextTarget?.dataset.settingAction) return;
                        if (!isReadOnly) {
                          void saveInputSetting(key, { closeEditor: !isInvalid });
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !isReadOnly && !isInvalid) {
                          void saveInputSetting(key, { closeEditor: true });
                        }
                      }}
                      className={`w-full pr-10 ${isInvalid ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      aria-invalid={isInvalid}
                      disabled={isReadOnly}
                    />
                    {isInvalid && (
                      <X
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-red-500"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="flex gap-2 sm:shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      data-setting-action="cancel"
                      onClick={() => {
                        setEditDraft(value && value !== 'false' ? value : '');
                        setEditingKey(null);
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      data-setting-action="save"
                      onClick={() => {
                        if (!isReadOnly && !isInvalid) {
                          void saveInputSetting(key, { closeEditor: true });
                        }
                      }}
                      disabled={isReadOnly || isInvalid}
                    >
                      Guardar
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full">
                  <span className="text-sm text-slate-700 break-all">{value && value !== 'false' ? value : '—'}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingKey(key);
                      setEditDraft(value && value !== 'false' ? value : '');
                    }}
                    disabled={isReadOnly}
                  >
                    Editar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    const isReadOnly = !!readOnly || description.includes('[PRÓXIMAMENTE]');

    return (
      <div key={key} className="rounded-2xl border border-indigo-100 bg-white/90 px-5 py-4 shadow-sm flex items-center justify-between">
        <div className="pr-4">
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-sm text-slate-500">{description}</div>
        </div>
        <Switch checked={value === 'true'} onCheckedChange={() => { void toggleSetting(key); }} disabled={isReadOnly} />
      </div>
    );
  };

  return (
    <div className="panel-card panel-card--main p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderLanguageRow()}
        {EVENT_SETTINGS.map(({ key, title, description, componentType, readOnly }) => (
          panelRole === 'superadmin'
            ? renderSuperadminRow(key as EventSettingsKey, title, description, componentType, readOnly)
            : renderAdminRow(key as EventSettingsKey, title, description, componentType)
        ))}
      </div>
    </div>
  );
};

export default EventSettings;
